<template>
  <div class="dengke-machine-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('dengkeMachine.page.title')" :subtitle="$t('dengkeMachine.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-refresh" @click="refresh()">
          {{ $t('common.refresh') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- KPI 统计 -->
    <el-row :gutter="16" class="ds-kpi-row">
      <el-col :xs="12" :sm="12" :md="6" :lg="6">
        <ds-kpi-card :label="$t('dengkeMachine.stats.total')" :value="stats.total" tone="info" />
      </el-col>
      <el-col :xs="12" :sm="12" :md="6" :lg="6">
        <ds-kpi-card :label="$t('dengkeMachine.stats.unit')" :value="stats.unit" tone="primary" />
      </el-col>
      <el-col :xs="12" :sm="12" :md="6" :lg="6">
        <ds-kpi-card :label="$t('dengkeMachine.stats.perimeter')" :value="stats.perimeter" tone="warning" />
      </el-col>
      <el-col :xs="12" :sm="12" :md="6" :lg="6">
        <ds-kpi-card :label="$t('dengkeMachine.stats.online')" :value="stats.online" tone="success" />
      </el-col>
    </el-row>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="4">
          <el-input v-model="conditions.buildingNum" clearable class="w-full"
            :placeholder="$t('dengkeMachine.search.buildingNum')" @keyup.enter.native="search()" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="4">
          <el-input v-model="conditions.unitNum" clearable class="w-full"
            :placeholder="$t('dengkeMachine.search.unitNum')" @keyup.enter.native="search()" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="4">
          <el-input v-model="conditions.machineAlias" clearable class="w-full"
            :placeholder="$t('dengkeMachine.search.machineAlias')" @keyup.enter.native="search()" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="4">
          <el-input v-model="conditions.machineCode" clearable class="w-full"
            :placeholder="$t('dengkeMachine.search.machineCode')" @keyup.enter.native="search()" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="4">
          <el-input v-model="conditions.machineIp" clearable class="w-full"
            :placeholder="$t('dengkeMachine.search.machineIp')" @keyup.enter.native="search()" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="4">
          <el-select v-model="conditions.onlineStatus" clearable class="w-full"
            :placeholder="$t('dengkeMachine.search.onlineStatus')">
            <el-option :label="$t('dengkeMachine.table.online')" value="在线" />
            <el-option :label="$t('dengkeMachine.table.offline')" value="离线" />
          </el-select>
        </el-col>
      </el-row>
      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="search()">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="resetConditions()">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- 应用类型切换 -->
    <div class="ds-panel ds-tabs-panel">
      <el-radio-group v-model="applyType" size="small" @change="changeApplyType">
        <el-radio-button label="">{{ $t('dengkeMachine.tab.all') }}</el-radio-button>
        <el-radio-button label="单元">{{ $t('dengkeMachine.tab.unit') }}</el-radio-button>
        <el-radio-button label="围墙">{{ $t('dengkeMachine.tab.perimeter') }}</el-radio-button>
      </el-radio-group>
    </div>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false"
        :show-refresh="true" @update:density="density = $event" @refresh="_listDengkeMachines(page.current, page.size)">
        <template #left>
          <span class="ds-panel__title">{{ $t('dengkeMachine.list.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="dengkeMachines" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('dengkeMachine.page.empty')"
        @retry="_listDengkeMachines(page.current, page.size)">
        <el-table-column prop="machineAlias" :label="$t('dengkeMachine.table.machineAlias')" min-width="180" show-overflow-tooltip />
        <el-table-column prop="machineCode" :label="$t('dengkeMachine.table.machineCode')" align="center" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.machineCode || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="productName" :label="$t('dengkeMachine.table.productName')" align="center" width="140" show-overflow-tooltip />
        <el-table-column prop="applyType" :label="$t('dengkeMachine.table.applyType')" align="center" width="110" />
        <el-table-column prop="locationDetail" :label="$t('dengkeMachine.table.location')" min-width="200" show-overflow-tooltip />
        <el-table-column prop="buildingNum" :label="$t('dengkeMachine.table.buildingNum')" align="center" width="90">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.buildingNum || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="unitNum" :label="$t('dengkeMachine.table.unitNum')" align="center" width="90">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.unitNum || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="machineIp" :label="$t('dengkeMachine.table.machineIp')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.machineIp || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="onlineStatus" :label="$t('dengkeMachine.table.onlineStatus')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag size="sm" :type="scope.row.onlineStatus === '在线' ? 'success' : 'muted'"
              :label="scope.row.onlineStatus === '在线' ? $t('dengkeMachine.table.online') : $t('dengkeMachine.table.offline')" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('dengkeMachine.table.operation')" align="center" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="showDetail(scope.row)">{{ $t('dengkeMachine.detail.title') }}</el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :page="page.current" :size="page.size" :total="page.total" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <!-- 设备详情 -->
    <el-dialog :title="$t('dengkeMachine.detail.title')" :visible.sync="detailVisible" width="700px" append-to-body>
      <el-form label-width="140px" size="small" v-if="currentDetail">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.machineId')">{{ currentDetail.machineId }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.machineAlias')">{{ currentDetail.machineAlias }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.machineCode')">{{ currentDetail.machineCode }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.did')">{{ currentDetail.did }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.communityId')">{{ currentDetail.communityId }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.subsystem')">{{ currentDetail.subsystem }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.productName')">{{ currentDetail.productName }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.productType')">{{ currentDetail.productType }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.applyType')">{{ currentDetail.applyType }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.direction')">{{ currentDetail.direction }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.locationDetail')">{{ currentDetail.locationDetail }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.machineIp')">{{ currentDetail.machineIp }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.factory')">{{ currentDetail.factory }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.onlineStatus')">{{ currentDetail.onlineStatus }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.serialNumber')">{{ currentDetail.serialNumber }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.macAddress')">{{ currentDetail.macAddress }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.hardwareVersion')">{{ currentDetail.hardwareVersion }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.mainVersion')">{{ currentDetail.mainVersion }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.faceVersion')">{{ currentDetail.faceVersion }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.fingerVersion')">{{ currentDetail.fingerVersion }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.buildingNum')">{{ currentDetail.buildingNum }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.unitNum')">{{ currentDetail.unitNum }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.icCardNum')">{{ currentDetail.icCardNum }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.faceNum')">{{ currentDetail.faceNum }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.fingerNum')">{{ currentDetail.fingerNum }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.bluetoothNum')">{{ currentDetail.bluetoothNum }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.qrcodeNum')">{{ currentDetail.qrcodeNum }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('dengkeMachine.detail.createTime')">{{ currentDetail.createTime }}</el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div slot="footer">
        <el-button @click="detailVisible = false">{{ $t('common.close') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listDengkeMachines } from '@/api/machine/dengkeMachineApi'
import { getCommunityId } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsKpiCard from '@/components/ds/ds-kpi-card.vue'

export default {
  name: 'DengkeMachineList',
  components: {
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag,
    DsKpiCard
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      communityId: '',
      applyType: '',
      dengkeMachines: [],
      detailVisible: false,
      currentDetail: null,
      stats: {
        total: 0,
        unit: 0,
        perimeter: 0,
        online: 0
      },
      conditions: {
        buildingNum: '',
        unitNum: '',
        machineAlias: '',
        machineCode: '',
        machineIp: '',
        onlineStatus: ''
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.refresh()
  },
  methods: {
    /**
     * 加载设备列表，同时统计各类型数量
     */
    async refresh() {
      await this._listDengkeMachines(this.page.current, this.page.size)
      await this._loadStats()
    },
    async _listDengkeMachines(page, rows) {
      this.loading = true
      this.hasError = false
      try {
        const params = {
          page: page,
          row: rows,
          communityId: this.communityId,
          applyType: this.applyType,
          buildingNum: this.conditions.buildingNum,
          unitNum: this.conditions.unitNum,
          machineAlias: this.conditions.machineAlias,
          machineCode: this.conditions.machineCode,
          machineIp: this.conditions.machineIp,
          onlineStatus: this.conditions.onlineStatus
        }
        const { data, total } = await listDengkeMachines(params)
        this.dengkeMachines = data
        this.page.total = total
      } catch (error) {
        console.error(this.$t('dengkeMachine.message.fetchError'), error)
        this.dengkeMachines = []
        this.page.total = 0
        this.hasError = true
        this.errorMessage = this.$t('common.loadError')
      } finally {
        this.loading = false
      }
    },
    /**
     * 统计数据：总数 / 单元 / 围墙 / 在线
     */
    async _loadStats() {
      try {
        const baseParams = {
          page: 1,
          row: 1000,
          communityId: this.communityId
        }
        const [allRes, unitRes, perimeterRes] = await Promise.all([
          listDengkeMachines(baseParams),
          listDengkeMachines({ ...baseParams, applyType: '单元' }),
          listDengkeMachines({ ...baseParams, applyType: '围墙' })
        ])
        this.stats.total = allRes.total
        this.stats.unit = unitRes.total
        this.stats.perimeter = perimeterRes.total
        this.stats.online = (allRes.data || []).filter(item => item.onlineStatus === '在线').length
      } catch (error) {
        console.error(this.$t('dengkeMachine.message.fetchError'), error)
      }
    },
    search() {
      this.page.current = 1
      this._listDengkeMachines(this.page.current, this.page.size)
    },
    changeApplyType() {
      this.page.current = 1
      this._listDengkeMachines(this.page.current, this.page.size)
    },
    resetConditions() {
      this.conditions = {
        buildingNum: '',
        unitNum: '',
        machineAlias: '',
        machineCode: '',
        machineIp: '',
        onlineStatus: ''
      }
      this.page.current = 1
      this._listDengkeMachines(this.page.current, this.page.size)
    },
    showDetail(row) {
      this.currentDetail = row
      this.detailVisible = true
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this._listDengkeMachines(this.page.current, this.page.size)
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._listDengkeMachines(this.page.current, this.page.size)
    }
  }
}
</script>

<style lang="scss" scoped>
.dengke-machine-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-kpi-row {
  margin-bottom: var(--ds-spacing-md);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-tabs-panel {
  padding: var(--ds-spacing-sm) var(--ds-spacing-lg);
}

.w-full {
  width: 100%;
}
</style>
